/**
 * On-position Auto B/E indicator.
 *
 * - "Auto B/E ON": the shared setting is on and the engine is watching this
 *   position (selected account, has a TP and an SL, hedge mode off).
 * - "B/E done":    the engine moved this position's stop to entry.
 * - "B/E failed":  the engine's move was refused; it will not retry.
 * - nothing:       the setting is off, or this position is not in scope.
 *
 * Purely a view of engine state (see store/autoBeStore.ts); it sends nothing.
 */
import React from 'react';
import { BrokerOrder, BrokerPosition, useBrokerStore } from '../../store/brokerStore';
import { autoBeStatusFor, useAutoBeStore } from '../../store/autoBeStore';
import './AutoBeBadge.css';

const WORKING = new Set(['Working', 'Accepted', 'Pending']);

/** TP/SL presence for a position, with the overlay's own order rules. */
export function bracketPresence(position: BrokerPosition, orders: BrokerOrder[]): { hasTp: boolean; hasSl: boolean } {
  const exitAction = position.netPos > 0 ? 'Sell' : 'Buy';
  const mine = orders.filter(o =>
    WORKING.has(o.ordStatus) &&
    o.action === exitAction &&
    o.accountId === position.accountId &&
    (!o.brokerId || !position.brokerId || o.brokerId === position.brokerId) &&
    (!o.contractId || !position.contractId || o.contractId === position.contractId),
  );
  const type = (o: BrokerOrder) => o.ordType?.toLowerCase() ?? '';
  return {
    hasTp: mine.some(o => type(o).includes('limit') && !type(o).includes('stop') && !!o.price),
    hasSl: mine.some(o => type(o).includes('stop') && !!o.stopPrice),
  };
}

export interface AutoBeBadgeProps {
  position: BrokerPosition;
  orders: BrokerOrder[];
  className?: string;
}

const LABELS = { armed: 'Auto B/E ON', done: 'B/E done', failed: 'B/E failed' } as const;
const TITLES = {
  armed: 'Auto break-even is armed: the engine moves the SL to entry at the trigger',
  done: 'Auto break-even moved the SL to entry for this position',
  failed: 'Auto break-even could not move the SL; it will not retry',
} as const;

const AutoBeBadge: React.FC<AutoBeBadgeProps> = ({ position, orders, className }) => {
  const enabled = useAutoBeStore(s => s.enabled);
  const fired = useAutoBeStore(s => s.fired);
  const failed = useAutoBeStore(s => s.failed);
  const hedgeActive = useBrokerStore(s => s.isHedgeCopyModeActive());
  const inScope = useBrokerStore(s =>
    (s.brokers ?? []).some(b => b.id === position.brokerId && b.selectedAccountId === position.accountId),
  );
  if (!position || position.netPos === 0) return null;

  const { hasTp, hasSl } = bracketPresence(position, orders);
  const status = autoBeStatusFor({
    enabled, fired, failed,
    brokerId: position.brokerId, accountId: position.accountId, contractId: position.contractId,
    inScope, hasTp, hasSl, hedgeActive,
  });
  if (!status) return null;

  return (
    <span
      className={`auto-be-badge ${status}${className ? ` ${className}` : ''}`}
      title={TITLES[status]}
      data-testid="auto-be-badge"
    >
      {LABELS[status]}
    </span>
  );
};

export default AutoBeBadge;
